import React, { useRef } from 'react';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Name, Title, Info, Code, Actions, Toast,  API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/PrintImports';
import { Sale } from "models/SaleModel";
import { SaleOrder } from "models/SaleOrderModel";
import { SaleItem } from "models/SaleItemModel";
import { User } from 'models/UserModel';
import { Doctor } from 'models/DoctorModel';
import { Client } from 'models/ClientModel';
import { ItemInventory } from "models/ItemInventoryModel";
import { HtmlDataFormat } from "components/common/HtmlFormat"
import { MediaList } from 'models/BaseModel';
import { FormList } from "models/BaseModel";
import SignaturePad from "components/common/SignManager";

const SalePrint: React.FC = () => {
  const widthOrder = { clientname: 20, clientpay: 9, insurance1: 9, insurance2: 9, frame:9, highindex:8,  gift: 8, cradd:9, cruse:9, total: 10 }
  const widthItem = { product: 50, type: 10, price: 10,  discount: 10, gst:10, amount: 10 }
  const [formId, formName] = [Code.Form.Id.SALE, Code.Form.Name.SALE];
  const formTitle = 'Sale Form';
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef } = usePrintContext();
  const [saleData, setSaleData] = useState<Sale>({});
  const [itemInventoryData, setItemInventoryData] = useState<ItemInventory[]>([]); 
  const [clientData, setClientData] = useState<Client | null>(null);
  const [doctorData, setDoctorData] = useState<Doctor | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [saleOrderData, setSaleOrderData] = useState<SaleOrder[]>([]); 
  const [saleItemData, setSaleItemData] = useState<SaleItem[]>([]); 
  const [fileList, setFileList] = useState<MediaList>({});
  const [drawingPad, setDrawingPad] = useState<string | null>(''); 
  const [signData, setSignData] = useState<string>('');
  const [receiverName, setReceiverName] = useState<string>('');
  const [receiverPhone, setReceiverPhone] = useState<string>('');


  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasPrint(Privilege);
  const printRef = useRef<HTMLDivElement>(null);

  const companyId = Utility.Storage.CompanyId();
  const locationId = Utility.Storage.LocationId();
  const companyName = Utility.Storage.CompanyName();

  useEffect(() => {
    API.Data.GetByKeyId<Sale>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setSaleData((prevData) => ({
        ...prevData,
        ...formData,
      }));
      setClientData(await API.Data.GetClientCity(data.client_id));
      setDoctorData(await API.Data.GetDoctor(data.doctor_id));
      setUserData(await API.Data.GetUser(data.user_id));
      await saleOrderList(data.sale_id);
      await saleItemList(data.sale_id);
      await Files.List.ParseFile(data.file_list, setFileList);
      const parsedData: MediaList = JSON.parse(data.file_list!);

      const docName1 = parsedData?.document1;
      if (docName1) {
        const imageData1 = await Files.File.Content(docName1, "notepad");
        if (imageData1) {
          setDrawingPad(imageData1);
        }
      }
     
      // if (parsedData?.document1) {
      //   Files.ImageAPI.Load('document1', parsedData, Files.ImageAPI.Fetch, setDrawingPad);
      // }
    }})
    setLoading(false);
  }, []); 

  const saleOrderList = async (saleId: number | undefined) => {
    if (!saleId) return;  
      const url = API.Build.FormApiEndpint(Code.Form.Name.SALEORDER, 200, Name.SaleOrder.Id, Code.Option.Sort.ASC, {
      fields: Utility.Join.Fields([
        Name.SaleOrder.Id, 
        Name.Sale.Id,
        Name.Client.FullName,
        Name.Common.TypeName,
        Name.Common.StatusName,
        Name.Common.CreditName,
        Name.SaleOrder.ClientAmount,
        Name.SaleOrder.Insurer1Amount,
        Name.SaleOrder.Insurer2Amount,
        Name.SaleOrder.FrameAmount,
        Name.SaleOrder.HighIndexAmount,
        Name.SaleOrder.GiftAmount,
        Name.SaleOrder.CreditAdd,
        Name.SaleOrder.CreditUse,
        Name.SaleOrder.Payable,
        Name.SaleOrder.Data,
      ]),
      [Name.Sale.Id]: saleId,
    });
    await API.Data.List<SaleOrder>(url, setSaleOrderData, setError, setLoading);
  };

  const saleItemList = async (saleId: number | undefined) => {
    if (!saleId) return;  
      const url = API.Build.FormApiEndpint(Code.Form.Name.SALEITEM, 200, Name.SaleItem.Id, Code.Option.Sort.ASC, {
      fields: Utility.Join.Fields([
        Name.SaleItem.Id, 
        Name.Sale.Id,
        Name.SaleOrder.Id,
        Name.Product.Name,
        Name.Common.TypeName,
        Name.SaleItem.Utncode,
        Name.SaleItem.Skucode,
        Name.SaleItem.Price,
        Name.SaleItem.Quantity,
        Name.SaleItem.DiscountAmount,
        Name.SaleItem.Tax1Amount,
        Name.SaleItem.NetAmount,
        Name.SaleItem.Data,
        Name.Specification.Id,
      ]),
      [Name.Sale.Id]: saleId,
    });
    await API.Data.List<SaleItem>(url, setSaleItemData, setError, setLoading);
  };

  useEffect(() => {
      setReceiverName(Utility.Get.FullName(clientData, Code.Form.Name.CLIENT) || '')
      setReceiverPhone(clientData?.client_phone || '')
    }, [clientData]); 

  const getAmount = (Amount: string | number | undefined ) => {
    return Utility.Format.Amount(Amount).replace(".00","")
  };

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={formName} Privilege={Privilege} signData={signData} clientData={clientData} formTitle={formTitle} fileName='Sale-Form' printRef={printRef} setMessage={setMessage} setError={setError} />
        <Control.Print.Data ref={printRef}>
            <Control.Flex>
              <Control.Column size={[0,60,0,0]}>
                <Control.Box>
                  <Field.CompanyHead companyId={companyId} locationId={locationId} />
                </Control.Box>
                <Control.Box>
                  <Field.Value value={Utility.Get.FullName(clientData, Code.Form.Name.CLIENT).toUpperCase()} valueBold={true} />
                  <Field.Value value={Utility.Format.Phone(clientData?.client_phone)} />
                  <Field.Value value={clientData?.client_address || ''} />
                  <Field.Value value={`${clientData?.city_name || ''} ${clientData?.client_postal || ''}`} />
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,40,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.SubHead title={formTitle} />
                  <Control.Form.Barcode value={saleData.sale_number!} />       
                  <Field.Value title={Title.SNo} value={saleData.sale_number} />
                  <Field.Value title={Title.Date} value={Utility.Format.DateOnly(saleData.sale_date)} />
                  <Field.Value title={Title.Doctor} value={Utility.Get.FullName(doctorData,Code.Form.Name.DOCTOR)} />
                  <Field.Value title={Title.Dispenser} value={Utility.Get.FullName(userData,Code.Form.Name.USER)} />
                  <Field.Value title={Title.Client} value={`#${clientData?.client_number}`} />
                  <Field.Value title={Title.DOB} value={Utility.Format.DateOnly(clientData?.client_dob)} />
                </Control.Box>
              </Control.Column>
            </Control.Flex>
            <Control.Form.Table>
              <Control.Form.Title>
                <Control.Grid.Items values={[
                  ['Client Name', widthOrder.clientname], 
                  ['Client', widthOrder.clientpay, Code.Align.RIGHT], 
                  ['Ins.1', widthOrder.insurance1, Code.Align.RIGHT], 
                  ['Ins.2', widthOrder.insurance2, Code.Align.RIGHT], 
                  ['Case', widthOrder.frame, Code.Align.RIGHT], 
                  ['HIndex', widthOrder.highindex, Code.Align.RIGHT],
                  ['Gift', widthOrder.gift, Code.Align.RIGHT],
                  ['Cr.Add', widthOrder.cradd, Code.Align.RIGHT],
                  ['Cr.Use', widthOrder.cruse, Code.Align.RIGHT],
                  ['Total', widthOrder.total, Code.Align.RIGHT]
                ]} />
              </Control.Form.Title>
              <Control.Form.List>
                {saleOrderData.map((order, index) => { return (
                  <Control.Form.Index key={index} index={index}>
                    <Control.Form.Item>
                      <Control.Grid.Items values={[
                        [Utility.Get.FullName(order, Code.Form.Name.CLIENT), widthOrder.clientname],
                        [getAmount(order.saleorder_client_amount), widthOrder.clientpay, Code.Align.RIGHT],
                        [getAmount(order.saleorder_insurer1_amount), widthOrder.insurance1, Code.Align.RIGHT],
                        [getAmount(order.saleorder_insurer2_amount), widthOrder.insurance2, Code.Align.RIGHT],
                        [getAmount(order.frame_amount), widthOrder.frame, Code.Align.RIGHT],
                        [getAmount(order.highindex_amount), widthOrder.highindex, Code.Align.RIGHT],
                        [getAmount(order.gift_amount), widthOrder.gift, Code.Align.RIGHT],
                        [getAmount(order.credit_add_amount), widthOrder.cradd, Code.Align.RIGHT],
                        [getAmount(order.credit_use_amount), widthOrder.cruse, Code.Align.RIGHT],
                        [getAmount(order.saleorder_payable_amount), widthOrder.total, Code.Align.RIGHT],
                      ]} />
                    </Control.Form.Item>
                  </Control.Form.Index>
                )})}
              </Control.Form.List>
              <Control.Form.Footer>
                <Control.Grid.Items values={[
                  ['Total', widthOrder.clientname], 
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.saleorder_client_amount || 0), 0)), widthOrder.clientpay, Code.Align.RIGHT], 
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.saleorder_insurer1_amount || 0), 0)), widthOrder.insurance1, Code.Align.RIGHT], 
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.saleorder_insurer2_amount || 0), 0)), widthOrder.insurance2, Code.Align.RIGHT], 
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.frame_amount || 0), 0)), widthOrder.frame, Code.Align.RIGHT], 
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.highindex_amount || 0), 0)), widthOrder.highindex, Code.Align.RIGHT],
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.gift_amount || 0), 0)), widthOrder.gift, Code.Align.RIGHT],
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.credit_add_amount || 0), 0)), widthOrder.cradd, Code.Align.RIGHT],
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.credit_use_amount || 0), 0)), widthOrder.cruse, Code.Align.RIGHT],
                  [getAmount(saleOrderData.reduce((sum, o) => sum + (o.saleorder_payable_amount || 0), 0)), widthOrder.total, Code.Align.RIGHT]
                ]} />
              </Control.Form.Footer>
            </Control.Form.Table>
            <Control.Box>
                {saleOrderData.map((order, orderIndex) => {
                  const currentItems = saleItemData?.filter((d) => d.saleorder_id === order.saleorder_id) || [];

                  let dataObject: FormList = {};
                  let isOrderGenerated =  false;
                  try {
                    const jsonData = order.saleorder_data;
                    if(jsonData){
                      const parsed = JSON.parse(jsonData);
                      if (typeof parsed === "object" && !Array.isArray(parsed)) {
                        dataObject = parsed;
                        isOrderGenerated = dataObject.order1 != null && dataObject.order1.id != 2;
                      }
                    }
                    
                  } catch (err) { console.error("Invalid JSON in saleorder_data:", order.saleorder_data); }

                  return (
                    <>
                      <Control.Grid.HashTarget hashId={order.saleorder_id} />
                      <Control.Form.BoxPanel key={orderIndex} index={orderIndex} className='br0 bs0 pa0'>
                        <div className='fs14'>Name: <span className='fwb ttc'>{Utility.Get.FullName(order, Code.Form.Name.CLIENT)}</span></div>
                        <Control.Form.Table>
                          <Control.Form.Title>
                            <Control.Grid.Items values={[
                              ["Product/Specifications", widthItem.product], 
                              [Title.Type, widthItem.type, Code.Align.RIGHT], 
                              ['Price/Qty.', widthItem.price, Code.Align.RIGHT], 
                              [Title.Discount, widthItem.discount, Code.Align.RIGHT],
                              [Title.GST, widthItem.gst, Code.Align.RIGHT],
                              [Title.Amount, widthItem.amount, Code.Align.RIGHT]
                            ]} />
                          </Control.Form.Title>
                          <Control.Form.List>
                            {currentItems.map((item, itemIndex) => (
                              <Control.Form.Index key={itemIndex} index={itemIndex}>
                                <Control.Form.OrderTextArea 
                                  iframeRef={iframeRef} 
                                  index={itemIndex} 
                                  specification={item.specification_id}
                                  data={HtmlDataFormat(item.specification_id, item.saleitem_data)}
                                  productName={item.product_name} 
                                  utncode={item.saleitem_utncode}
                                  skucode={item.saleitem_skucode} 
                                  frameStatus={Utility.Remove.NA(item.type_name)}
                                  price={item.saleitem_price}
                                  quantity={item.saleitem_quantity}
                                  discAmount={item.saleitem_discount_amount}
                                  discPercentage={item.saleitem_discount_percentage}
                                  tax1Amount={item.saleitem_tax1_amount}
                                  tax1Percentage={item.saleitem_tax1_percentage}
                                  netAmount={item.saleitem_net_amount}
                                />
                              </Control.Form.Index>
                            ))}
                          </Control.Form.List>
                        </Control.Form.Table>
                        <Control.Flex>
                          <Control.Column size={[0,80,0,1]}><></></Control.Column>
                          <Control.Column size={[0,20,0,1]}>
                            <Control.Row>
                              <Field.Title title={"Total"} size={[0,50,1]} align={Code.Align.RIGHT} />
                              <Field.Text id={Name.SaleOrder.Payable} value={order.saleorder_payable_amount} size={[1,50,0]} alignRight={true} disabled={true}  />
                            </Control.Row>
                          </Control.Column>
                        </Control.Flex>
                      </Control.Form.BoxPanel>
                    </>
                  );
                })}
              </Control.Box>
              {drawingPad ? <div className='box mb4'><img className='box ma br' src={drawingPad} alt="Doctor Sign" /></div> : null} 
              <Control.Flex>
                <Control.Column size={[0,70,0,0]}>
                  <Control.Box>
                    <Control.Print.Term title={'Terms & Conditions:'} value={`<p>• I consent to ${companyName} for the secure electronic use and storage of my health information in accordance with applicable health privacy laws, and to related communications using my contact details. Any changes in my family group must be reported to the store.</p><p>• I agree to all the terms and conditions mentioned above.</p> ${companyId == 10 ? '<p class="fwb">• This is final sale. No Refund, No Exchange.</p><p class="fwb">• Vente finale. Aucun remboursement, aucun échange.</p>' : ''}`} />
                  </Control.Box>
                </Control.Column>
              </Control.Flex>
              <SignaturePad signData={signData} setSignData={setSignData} receiverName={receiverName} setReceiverName={setReceiverName} receiverPhone={receiverPhone} setReceiverPhone={setReceiverPhone} />
            <Field.PrintedOn />
        </Control.Print.Data>    
      </>)}
      {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Print />
    </Control.Print.Frame>
  );
};

export default SalePrint;